<template>
  <standard-dialog
    v-model="model"
    :title="editedItem.id ? $t('change_grocery') : $t('new_grocery')"
    icon="mdi-food-apple-outline"
    @save="save"
    :disabled="!valid || loading"
    :loading="loading"
  >
    <v-container>
      <v-form v-model="valid">
        <v-row>
          <v-col cols="12">
            <text-field
              v-model="editedItem.name"
              :label="$t('title')"
              :rules="[rules.required]"
              required
            ></text-field>
          </v-col>
          <v-row class="px-3">
            <v-col cols="6">
              <text-field
                v-model="editedItem.code"
                :label="$t('item_code')"
                :rules="[rules.required]"
                required
              ></text-field>
            </v-col>
            <v-col cols="6">
              <autocomplete
                v-model="editedItem.measure"
                required
                :items="[
                  { code: 1, name: 'kg' },
                  { code: 2, name: 'l' },
                  { code: 3, name: 'kom' },
                ]"
                :label="$t('unit_of_measurment')"
                :rules="[rules.required]"
                item-value="name"
                item-text="name"
              ></autocomplete>
            </v-col>
          </v-row>
          <v-row class="px-3">
            <v-col cols="6">
              <text-field
                type="number"
                v-model="editedItem.price"
                :label="$t('price')"
              ></text-field>
            </v-col>
            <v-col cols="6">
              <text-field
                type="number"
                v-model="editedItem.min_quantity"
                :label="$t('minimal_qty')"
                :hint="$t('minimum_stock_hint')"
                persistent-hint
              ></text-field>
            </v-col>
          </v-row>
        </v-row>

        <v-row class="px-3 mt-4" style="width: 100%">
          <v-col cols="12">
            <div class="subtitle-1 font-weight-medium mb-2 text--primary">
              {{ $t('grocery_gallery') || 'Galerija proizvoda' }}
            </div>

            <input
              ref="fileInput"
              type="file"
              accept="image/*"
              multiple
              class="d-none"
              @change="onImagesSelected"
            />

            <v-card
              outlined
              class="upload-zone d-flex flex-column align-center justify-center py-8 px-4 text-center mb-4"
              :class="{ 'upload-zone-error': imageFiles.length > 5 }"
              ripple
              @click="triggerFileInput"
            >
              <v-icon size="48" color="primary" class="mb-2">mdi-cloud-upload-outline</v-icon>
              <div class="body-1 font-weight-bold grey--text text--darken-2">
                Prebrzite slike ovde ili <span class="primary--text">kliknite za pretragu</span>
              </div>
              <div class="caption text--secondary mt-1">
                Podržani formati: JPG, PNG, WEBP (Maksimalno 5 slika)
              </div>
            </v-card>

            <div v-if="imageFiles.length > 5" class="error--text caption pl-3 mb-4">
              Maksimalno možete izabrati 5 slika. Trenutno ste izabrali {{ imageFiles.length }}.
            </div>
          </v-col>

          <v-col cols="12" v-if="imagePreviews.length > 0">
            <v-row dense>
              <v-col
                v-for="(img, index) in imagePreviews"
                :key="index"
                cols="4"
                sm="3"
                md="2"
              >
                <v-hover v-slot="{ hover }">
                  <v-card outlined class="preview-card" :class="{ 'upload-failed': img.error }" style="position: relative;">
                    <v-img :src="img.src" aspect-ratio="1" class="grey lighten-4 rounded">

                      <v-chip v-if="index === 0 && !img.loading" color="primary" x-small class="main-image-badge">
                        Glavna
                      </v-chip>

                      <div v-if="img.loading" class="upload-overlay d-flex flex-column align-center justify-center">
                        <v-progress-circular
                          :value="img.progress"
                          color="primary"
                          size="32"
                          width="3"
                        ></v-progress-circular>
                        <span class="caption white--text mt-1">{{ img.progress }}%</span>
                      </div>

                      <div v-if="img.error" class="upload-overlay error-overlay d-flex flex-column align-center justify-center">
                        <v-icon color="white">mdi-alert-circle-outline</v-icon>
                        <span class="caption white--text mt-1">Neuspešno</span>
                      </div>

                      <v-fade-transition v-if="!img.loading">
                        <div
                          v-if="hover"
                          class="d-flex v-card--reveal white--text"
                          style="height: 100%; background: rgba(0, 0, 0, 0.4); position: absolute; top: 0; width: 100%; align-items: center; justify-content: center;"
                        >
                          <v-btn
                            fab
                            dark
                            x-small
                            color="error"
                            @click.stop="removeImage(index, img.id)"
                          >
                            <v-icon small>mdi-delete</v-icon>
                          </v-btn>
                        </div>
                      </v-fade-transition>

                    </v-img>
                  </v-card>
                </v-hover>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

      </v-form>
    </v-container>
  </standard-dialog>
</template>

<script>
import StandardDialog from "@/components/dialogs/StandardDialog.vue";
import { defaultGrocery } from "@/models/food";
import hasModel from "@/mixins/hasModel";
import hasValidation from "@/mixins/hasValidation";
import hasSnackbar from "@/mixins/hasSnackbar";
import groceryService from "@/services/grocery";
import axios from "../../../plugins/axios";
import conf from "@/config.json";

export default {
  name: "GroceryDialog",
  mixins: [hasModel, hasValidation, hasSnackbar],
  components: {
    StandardDialog,
  },
  props: {
    editedItem: {
      type: Object,
      required: true,
      default: () => ({ ...defaultGrocery }),
    },
  },
  data: () => ({
    loading: false,

    valid: false,
    imageFiles: [],
    imagePreviews: []
  }),
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    onImagesSelected(event) {
      const selectedFiles = Array.from(event.target.files);
      if (!selectedFiles.length) return;

      selectedFiles.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const itemIndex = this.imagePreviews.push({
            id: null,
            src: e.target.result,
            loading: true,
            progress: 0,
            error: false
          }) - 1;

          this.uploadSingleImage(file, itemIndex);
        };
        reader.readAsDataURL(file);
      });

      this.$refs.fileInput.value = '';
    },
    uploadSingleImage(file, index) {
      const formData = new FormData();
      formData.append('image', file);

      const supplierId = this.user.type === 'supplier' ? this.user.suppliers[0].id : null;
      if (supplierId) {
        formData.append('supplier_id', supplierId);
      }
      else {
        formData.append('restaurant_id', this.user.restaurants[0].id);
      }

      axios.post('/gallery/grocery', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          this.imagePreviews[index].progress = percentCompleted;
        }
      })
      .then(response => {
        const image = response.data;
        const isSupplier = this.user?.type === 'supplier';
        const entityId = isSupplier
          ? this.user?.suppliers?.[0]?.id
          : this.user?.restaurants?.[0]?.id;
        const entityPath = isSupplier ? 'suppliers' : 'restaurants';
        this.imagePreviews[index].id = image.id;
        this.imagePreviews[index].src = `${conf.apiUrlBase}/${entityPath}/${entityId}/small/${image.image_path}`;
        this.imagePreviews[index].loading = false;

        if(!this.editedItem.uploaded_image_ids) {
          this.editedItem.uploaded_image_ids = [];
        }
        this.editedItem.uploaded_image_ids.push(image.id);
      })
      .catch(error => {
        this.imagePreviews[index].loading = false;
        this.imagePreviews[index].error = true;
      });
    },
    removeImage(index, imageId) {
      this.imagePreviews.splice(index, 1);
      if (imageId) {
        this.editedItem.uploaded_image_ids = this.editedItem.uploaded_image_ids.filter(id => id !== imageId);
        axios.delete(`/gallery/${imageId}`);
      }
    },
    close() {
      this.editedItem = { ...defaultGrocery };
      this.model = false;
    },
    save() {
      this.editedItem.parent_id = this.editedItem.parent ? this.editedItem.parent.id : 0;
      if (this.editedItem.id) {
        this.updateGrocery();
      } else {
        this.createGrocery();
      }
    },
    updateGrocery() {
      this.loading = true;
      this.handleRequest(
        groceryService.updateGrocery(this.editedItem.id, { ...this.editedItem, images: this.imagePreviews.map(image => image.id)}),
        {
          successMessage: this.$t("ingredient_updated"),
          errorMessagePrefix: this.$t("error") + ": " || "Error: ",
        }
      )
        .then((res) => {
          this.$emit("groceryUpdated", res.data);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    createGrocery() {
      this.loading = true;
      this.handleRequest(groceryService.createGrocery({ ...this.editedItem, images: this.imagePreviews.map(image => image.id)}), {
        successMessage: this.$t("ingredient_created"),
        errorMessagePrefix: this.$t("error") + ": " || "Error: ",
      })
        .then((res) => {
          this.$emit("groceryCreated", res.data);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  watch: {
    model(val) {
      if (val && this.editedItem?.images?.length) {
        const isSupplier = this.user?.type === 'supplier';
        const entityId = isSupplier
          ? this.user?.suppliers?.[0]?.id
          : this.user?.restaurants?.[0]?.id;
        const entityPath = isSupplier ? 'suppliers' : 'restaurants';
        this.imagePreviews = this.editedItem.images.map(image => ({
          id: image.id,
          src: `${conf.apiUrlBase}/${entityPath}/${entityId}/small/${image.image_path}`,
          loading: false,
          progress: 100,
          error: false,
        }));
      } else if (!val) {
        this.imagePreviews = [];
        this.imageFiles = [];
      }
    },
  },
  computed: {
    user() {
      return this.$store.state.user.user;
    },
  },
};
</script>

<style scoped>
/* Sloj koji prekriva sliku dok traje upload ili ako pukne greška */
.upload-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1;
}

.error-overlay {
  background: rgba(211, 47, 47, 0.8); /* Crvenkasta prozirna za grešku */
}

.upload-failed {
  border: 1px solid var(--v-error-base) !important;
}

.preview-card {
  border-radius: 6px !important;
  overflow: hidden;
}

.main-image-badge {
  position: absolute;
  top: 6px;
  left: 6px;
  z-index: 2;
}
</style>
